import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Container from '../../components/Container';
import Header from '../../components/Header';

type BtnProps = {
    color?: 'primary' | 'secondary' | 'danger' | 'warn';
    onClick?: () => void;
    disabled?: boolean;
    children: React.ReactNode;
};
const Btn: React.FC<BtnProps> = ({ color = 'secondary', onClick, disabled, children }) => {
    const bg = color === 'primary' ? '#4a9eff' :
        color === 'danger' ? '#b3392f' :
        color === 'warn' ? '#ff9800' : '#2a3140';
    const fg = color === 'warn' ? '#1b1b1b' : '#ffffff';
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            style={{
                background: disabled ? '#1c2230' : bg,
                color: disabled ? '#6f7689' : fg,
                border: 0,
                borderRadius: 6,
                padding: '0.4rem 0.85rem',
                fontSize: '0.85rem',
                fontWeight: 600,
                cursor: disabled ? 'not-allowed' : 'pointer',
            }}>
            {children}
        </button>
    );
};


type FlexProps = {
    direction?: 'row' | 'column';
    gap?: string;
    wrap?: boolean;
    children: React.ReactNode;
};
const Flex: React.FC<FlexProps> = ({ direction = 'row', gap = '0.5rem', wrap = false, children }) => (
    <div style={{
        display: 'flex',
        flexDirection: direction,
        gap,
        flexWrap: wrap ? 'wrap' : 'nowrap',
        alignItems: direction === 'row' ? 'center' : 'stretch',
    }}>{children}</div>
);


/**
 * New employee time-management module. Lives at /timeclock alongside
 * /payroll (legacy). Admin-only. Calls /api/timeclock/* — all routes
 * gated by PayrollAdminGuard server-side.
 *
 * Design notes:
 *  - Top cards summarize current range.
 *  - Tabs flip between Current / Previous / Custom / Open / Needs Review / Detail.
 *  - Edit/Void/Manual/Close are modals; each requires a typed reason.
 *  - Audit history is a modal too; calls /punches/:id/audit.
 *  - CSV downloads are direct GETs with credentials.
 */

type PeriodSummaryRow = {
    employeeId: string;
    name: string;
    email: string;
    hourlyRate: number;
    regularHours: number;
    overtimeHours: number;
    totalHours: number;
    paidHours: number;
    shiftCount: number;
    openOrFlaggedShifts: number;
    estimatedGross: number;
};

type PeriodSummary = {
    rangeKind: 'current' | 'previous' | 'custom';
    periodStartUtc: string;
    periodEndUtc: string;
    timezone: string;
    periodType: string;
    overtimeThresholdHours: number;
    rows: PeriodSummaryRow[];
    totals: {
        totalHours: number;
        regularHours: number;
        overtimeHours: number;
        estimatedGross: number;
        shiftCount: number;
        employeeCount: number;
    };
};

type ShiftRow = {
    _id: string;
    employeeId: string;
    employeeName: string;
    clockInUtc: string;
    clockOutUtc: string | null;
    clockInLocal: string;
    clockInLocalDate: string;
    clockOutLocal: string;
    totalHours: number | null;
    paidHours: number | null;
    unpaidBreakMinutes: number;
    status: string;
    source: string;
    adminNotes: string;
    needsReview: boolean;
    flaggedReason: string;
    editedByAdminName: string;
    editReason: string;
    originalClockInUtc: string | null;
    originalClockOutUtc: string | null;
    voidedAt: string | null;
    voidedByAdminName: string;
    voidReason: string;
};

type Dashboard = {
    summary: PeriodSummary;
    openShifts: ShiftRow[];
    needsReview: ShiftRow[];
    now: string;
};

type Employee = {
    _id: string;
    name: string;
    email: string;
    hourlyRate: number;
    role?: string;
    active: boolean;
};

const ROUND_2 = (n: number) => Math.round(n * 100) / 100;
const FORMAT_RANGE = (sIso: string, eIso: string) => {
    if (!sIso || !eIso) return '';
    const s = new Date(sIso);
    const e = new Date(eIso);
    const opts: any = { month: 'short', day: 'numeric', year: 'numeric' };
    // periodEndUtc is the exclusive boundary — show the day before for display.
    const eShown = new Date(e.getTime() - 1);
    return `${s.toLocaleDateString('en-US', opts)} — ${eShown.toLocaleDateString('en-US', opts)}`;
};

const STATUS_BADGE: Record<string, { label: string; bg: string; fg: string }> = {
    OPEN: { label: 'Open', bg: '#1f3a25', fg: '#9af5b3' },
    CLOSED: { label: 'Closed', bg: '#1c2230', fg: '#bdd7ff' },
    EDITED: { label: 'Edited', bg: '#2a2a3a', fg: '#cdb8ec' },
    NEEDS_REVIEW: { label: 'Needs Review', bg: '#3a2c14', fg: '#ffae3a' },
    VOIDED: { label: 'Voided', bg: '#3a1c1c', fg: '#ff8a8a' },
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
    const s = STATUS_BADGE[status] || { label: status, bg: '#222', fg: '#ddd' };
    return (
        <span style={{
            display: 'inline-block', padding: '2px 8px', borderRadius: 999,
            fontSize: '0.75rem', fontWeight: 600, letterSpacing: '0.3px',
            background: s.bg, color: s.fg,
        }}>{s.label}</span>
    );
};

const Card: React.FC<{ label: string; value: string | number; sub?: string }> = ({ label, value, sub }) => (
    <div style={{
        flex: '1 1 200px',
        background: '#131822',
        border: '1px solid #1f2735',
        borderRadius: 8,
        padding: '0.85rem 1rem',
    }}>
        <div style={{ color: '#9aa3b4', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</div>
        <div style={{ color: '#e2e7ef', fontSize: '1.55rem', fontWeight: 700, marginTop: 4 }}>{value}</div>
        {sub && <div style={{ color: '#9aa3b4', fontSize: '0.78rem', marginTop: 2 }}>{sub}</div>}
    </div>
);

type RangeKind = 'current' | 'previous' | 'custom';

const TimeClockAdminPage: React.FC = () => {
    const [range, setRange] = useState<RangeKind>('current');
    const [customFrom, setCustomFrom] = useState<string>('');
    const [customTo, setCustomTo] = useState<string>('');
    const [tab, setTab] = useState<'summary' | 'detail' | 'clockedIn' | 'review' | 'byEmployee'>('summary');
    const [dashboard, setDashboard] = useState<Dashboard | null>(null);
    const [shifts, setShifts] = useState<ShiftRow[] | null>(null);
    const [employees, setEmployees] = useState<Employee[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [selectedEmpId, setSelectedEmpId] = useState<string>('');

    // Modal state
    const [editing, setEditing] = useState<ShiftRow | null>(null);
    const [voiding, setVoiding] = useState<ShiftRow | null>(null);
    const [closing, setClosing] = useState<ShiftRow | null>(null);
    const [adding, setAdding] = useState(false);
    const [viewingAuditFor, setViewingAuditFor] = useState<string | null>(null);

    const rangeParams = useMemo(() => {
        if (range === 'custom') {
            const params: Record<string, string> = { range: 'custom' };
            if (customFrom) params.from = new Date(customFrom).toISOString();
            if (customTo) params.to = new Date(customTo).toISOString();
            return params;
        }
        return { range };
    }, [range, customFrom, customTo]);

    const flash = (msg: string, ok = true) => {
        if (ok) { setSuccess(msg); setError(null); }
        else { setError(msg); setSuccess(null); }
        window.setTimeout(() => { setSuccess(null); setError(null); }, 4000);
    };

    const loadDashboard = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await axios.get('/api/timeclock/dashboard', { params: rangeParams });
            setDashboard(res.data);
        } catch (e: any) {
            setError(e?.response?.data?.message || 'Failed to load dashboard');
        } finally { setLoading(false); }
    }, [rangeParams]);

    const loadShifts = useCallback(async () => {
        const params: Record<string, string> = { ...rangeParams };
        if (selectedEmpId) params.employeeId = selectedEmpId;
        try {
            const res = await axios.get('/api/timeclock/shifts', { params });
            setShifts(res.data.rows);
        } catch (e: any) {
            setError(e?.response?.data?.message || 'Failed to load shifts');
        }
    }, [rangeParams, selectedEmpId]);

    useEffect(() => {
        axios.get('/api/timeclock/employees').then(r => setEmployees(r.data)).catch(() => {});
    }, []);
    useEffect(() => { loadDashboard(); }, [loadDashboard]);
    useEffect(() => {
        if (tab === 'detail' || tab === 'byEmployee') loadShifts();
    }, [tab, loadShifts]);

    const totals = dashboard?.summary?.totals;
    const periodRange = dashboard
        ? FORMAT_RANGE(dashboard.summary.periodStartUtc, dashboard.summary.periodEndUtc)
        : '...';

    const onAction = async (fn: () => Promise<void>, successMsg: string) => {
        try {
            await fn();
            flash(successMsg, true);
            await loadDashboard();
            if (tab === 'detail' || tab === 'byEmployee') await loadShifts();
        } catch (e: any) {
            flash(e?.response?.data?.message || 'Action failed', false);
        }
    };

    // CSV download triggers a direct browser navigation so the
    // attachment filename header is honored.
    const downloadCsv = (kind: 'summary' | 'shifts') => {
        const qs = new URLSearchParams();
        qs.set('range', rangeParams.range);
        if (rangeParams.from) qs.set('from', rangeParams.from);
        if (rangeParams.to) qs.set('to', rangeParams.to);
        window.open(`/api/timeclock/export/${kind}.csv?` + qs.toString(), '_blank');
    };

    return (
        <Container containerName="timeclock-admin">
            <Header title="Time Clock" subtitle="Employee hours, edits, audit" />
            <Flex direction="column" gap="1rem">
                <h1 style={{ margin: 0 }}>Time Clock</h1>
                <div style={{
                    background: '#131822',
                    border: '1px solid #1f2735',
                    borderRadius: 6,
                    padding: '0.5rem 0.85rem',
                    color: '#9aa3b4',
                    fontSize: '0.82rem',
                }}>
                    Pay periods are locked to a biweekly Sunday&ndash;Saturday cycle anchored to 05/03/2026.
                </div>

                {error && <div style={{ background: '#3a1c1c', color: '#ff8a8a', padding: '0.6rem 0.9rem', borderRadius: 6 }}>{error}</div>}
                {success && <div style={{ background: '#1f3a25', color: '#9af5b3', padding: '0.6rem 0.9rem', borderRadius: 6 }}>{success}</div>}

                {/* Period filter row */}
                <Flex direction="row" gap="0.5rem" wrap>
                    <Btn color={range === 'current' ? 'primary' : 'secondary'} onClick={() => setRange('current')}>Current</Btn>
                    <Btn color={range === 'previous' ? 'primary' : 'secondary'} onClick={() => setRange('previous')}>Previous</Btn>
                    <Btn color={range === 'custom' ? 'primary' : 'secondary'} onClick={() => setRange('custom')}>Custom</Btn>
                    {range === 'custom' && (
                        <>
                            <input type="datetime-local" value={customFrom} onChange={e => setCustomFrom(e.target.value)} />
                            <input type="datetime-local" value={customTo} onChange={e => setCustomTo(e.target.value)} />
                            <Btn color="primary" onClick={() => loadDashboard()}>Apply</Btn>
                        </>
                    )}
                    <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                        <Btn onClick={() => downloadCsv('summary')}>Export summary CSV</Btn>
                        <Btn onClick={() => downloadCsv('shifts')}>Export shift detail CSV</Btn>
                        <Btn color="primary" onClick={() => setAdding(true)}>+ Add Manual Shift</Btn>
                    </div>
                </Flex>

                {/* Top cards */}
                <Flex direction="row" gap="0.75rem" wrap>
                    <Card label="Pay period" value={periodRange} sub={dashboard?.summary?.periodType} />
                    <Card label="Total hours" value={ROUND_2(totals?.totalHours ?? 0).toFixed(2)} sub={`reg ${ROUND_2(totals?.regularHours ?? 0).toFixed(2)} · OT ${ROUND_2(totals?.overtimeHours ?? 0).toFixed(2)}`} />
                    <Card label="Clocked in now" value={String(dashboard?.openShifts.length ?? 0)} />
                    <Card label="Needs review" value={String(dashboard?.needsReview.length ?? 0)} sub="open > 16h or admin-flagged" />
                    <Card label="Est. gross" value={'$' + ROUND_2(totals?.estimatedGross ?? 0).toFixed(2)} sub={`${totals?.shiftCount ?? 0} shifts · ${totals?.employeeCount ?? 0} employees`} />
                </Flex>

                {/* Tabs */}
                <Flex direction="row" gap="0.4rem" wrap>
                    {([
                        ['summary', 'Summary'],
                        ['detail', 'Shift Detail'],
                        ['clockedIn', `Clocked In (${dashboard?.openShifts.length ?? 0})`],
                        ['review', `Needs Review (${dashboard?.needsReview.length ?? 0})`],
                        ['byEmployee', 'By Employee'],
                    ] as const).map(([key, label]) => (
                        <Btn key={key} color={tab === key ? 'primary' : 'secondary'} onClick={() => setTab(key as any)}>{label}</Btn>
                    ))}
                </Flex>

                {loading && <div>Loading…</div>}

                {tab === 'summary' && dashboard && (
                    <SummaryTable summary={dashboard.summary} onPickEmployee={(empId) => { setSelectedEmpId(empId); setTab('byEmployee'); }} />
                )}

                {tab === 'detail' && shifts && (
                    <ShiftsTable
                        rows={shifts}
                        onEdit={setEditing}
                        onVoid={setVoiding}
                        onClose={setClosing}
                        onAudit={(id) => setViewingAuditFor(id)}
                    />
                )}

                {tab === 'clockedIn' && dashboard && (
                    <ShiftsTable
                        rows={dashboard.openShifts}
                        onEdit={setEditing}
                        onVoid={setVoiding}
                        onClose={setClosing}
                        onAudit={(id) => setViewingAuditFor(id)}
                    />
                )}

                {tab === 'review' && dashboard && (
                    <ShiftsTable
                        rows={dashboard.needsReview}
                        onEdit={setEditing}
                        onVoid={setVoiding}
                        onClose={setClosing}
                        onAudit={(id) => setViewingAuditFor(id)}
                    />
                )}

                {tab === 'byEmployee' && (
                    <>
                        <Flex direction="row" gap="0.5rem">
                            <select value={selectedEmpId} onChange={e => setSelectedEmpId(e.target.value)}>
                                <option value="">All employees</option>
                                {employees.map(e => (
                                    <option key={e._id} value={e._id}>{e.name}</option>
                                ))}
                            </select>
                            <Btn onClick={() => loadShifts()}>Reload</Btn>
                        </Flex>
                        {shifts && (
                            <ShiftsTable
                                rows={shifts}
                                onEdit={setEditing}
                                onVoid={setVoiding}
                                onClose={setClosing}
                                onAudit={(id) => setViewingAuditFor(id)}
                            />
                        )}
                    </>
                )}
            </Flex>

            {editing && (
                <EditShiftModal
                    shift={editing}
                    onCancel={() => setEditing(null)}
                    onSaved={() => { setEditing(null); onAction(async () => {}, 'Shift updated'); }}
                    flash={flash}
                />
            )}
            {voiding && (
                <VoidShiftModal
                    shift={voiding}
                    onCancel={() => setVoiding(null)}
                    onConfirmed={() => { setVoiding(null); onAction(async () => {}, 'Shift voided'); }}
                    flash={flash}
                />
            )}
            {closing && (
                <CloseShiftModal
                    shift={closing}
                    onCancel={() => setClosing(null)}
                    onConfirmed={() => { setClosing(null); onAction(async () => {}, 'Open shift closed'); }}
                    flash={flash}
                />
            )}
            {adding && (
                <AddManualShiftModal
                    employees={employees}
                    onCancel={() => setAdding(false)}
                    onSaved={() => { setAdding(false); onAction(async () => {}, 'Manual shift added'); }}
                    flash={flash}
                />
            )}
            {viewingAuditFor && (
                <AuditHistoryModal
                    shiftId={viewingAuditFor}
                    onClose={() => setViewingAuditFor(null)}
                />
            )}
        </Container>
    );
};

const SummaryTable: React.FC<{ summary: PeriodSummary; onPickEmployee: (id: string) => void }> = ({ summary, onPickEmployee }) => (
    <div style={{ overflowX: 'auto' }}>
        <table className="record-table" style={{ width: '100%', minWidth: 720 }}>
            <thead>
                <tr>
                    <th>Employee</th>
                    <th style={{ textAlign: 'right' }}>Regular h</th>
                    <th style={{ textAlign: 'right' }}>OT h</th>
                    <th style={{ textAlign: 'right' }}>Total h</th>
                    <th style={{ textAlign: 'right' }}>Paid h</th>
                    <th style={{ textAlign: 'right' }}>Shifts</th>
                    <th style={{ textAlign: 'right' }}>Open/Flagged</th>
                    <th style={{ textAlign: 'right' }}>Est. gross</th>
                    <th></th>
                </tr>
            </thead>
            <tbody>
                {summary.rows.map(r => (
                    <tr key={r.employeeId}>
                        <td>{r.name}</td>
                        <td style={{ textAlign: 'right' }}>{ROUND_2(r.regularHours).toFixed(2)}</td>
                        <td style={{ textAlign: 'right', color: r.overtimeHours > 0 ? '#ffae3a' : undefined }}>
                            {ROUND_2(r.overtimeHours).toFixed(2)}
                        </td>
                        <td style={{ textAlign: 'right', fontWeight: 600 }}>{ROUND_2(r.totalHours).toFixed(2)}</td>
                        <td style={{ textAlign: 'right' }}>{ROUND_2(r.paidHours).toFixed(2)}</td>
                        <td style={{ textAlign: 'right' }}>{r.shiftCount}</td>
                        <td style={{ textAlign: 'right', color: r.openOrFlaggedShifts > 0 ? '#ffae3a' : undefined }}>{r.openOrFlaggedShifts}</td>
                        <td style={{ textAlign: 'right' }}>${ROUND_2(r.estimatedGross).toFixed(2)}</td>
                        <td><Btn onClick={() => onPickEmployee(r.employeeId)}>Detail</Btn></td>
                    </tr>
                ))}
                <tr style={{ borderTop: '2px solid #2a3140', fontWeight: 700 }}>
                    <td>Totals</td>
                    <td style={{ textAlign: 'right' }}>{ROUND_2(summary.totals.regularHours).toFixed(2)}</td>
                    <td style={{ textAlign: 'right' }}>{ROUND_2(summary.totals.overtimeHours).toFixed(2)}</td>
                    <td style={{ textAlign: 'right' }}>{ROUND_2(summary.totals.totalHours).toFixed(2)}</td>
                    <td></td>
                    <td style={{ textAlign: 'right' }}>{summary.totals.shiftCount}</td>
                    <td></td>
                    <td style={{ textAlign: 'right' }}>${ROUND_2(summary.totals.estimatedGross).toFixed(2)}</td>
                    <td></td>
                </tr>
            </tbody>
        </table>
    </div>
);

const ShiftsTable: React.FC<{
    rows: ShiftRow[];
    onEdit: (s: ShiftRow) => void;
    onVoid: (s: ShiftRow) => void;
    onClose: (s: ShiftRow) => void;
    onAudit: (id: string) => void;
}> = ({ rows, onEdit, onVoid, onClose, onAudit }) => (
    <div style={{ overflowX: 'auto' }}>
        <table className="record-table" style={{ width: '100%', minWidth: 900 }}>
            <thead>
                <tr>
                    <th>Employee</th>
                    <th>Date</th>
                    <th>Clock in</th>
                    <th>Clock out</th>
                    <th>Hours</th>
                    <th>Status</th>
                    <th>Source</th>
                    <th>Notes</th>
                    <th>Actions</th>
                </tr>
            </thead>
            <tbody>
                {rows.length === 0 && (
                    <tr><td colSpan={9} style={{ color: '#9aa3b4', padding: '1rem' }}>No shifts.</td></tr>
                )}
                {rows.map(s => (
                    <tr key={s._id}>
                        <td>{s.employeeName}</td>
                        <td>{s.clockInLocalDate}</td>
                        <td>{s.clockInLocal}</td>
                        <td>{s.clockOutLocal || <em style={{ color: '#9af5b3' }}>open</em>}</td>
                        <td>{s.totalHours == null ? '—' : ROUND_2(s.totalHours).toFixed(2)}</td>
                        <td><StatusBadge status={s.status} /></td>
                        <td><span style={{ fontSize: '0.78rem', color: '#9aa3b4' }}>{s.source}</span></td>
                        <td style={{ maxWidth: 240, whiteSpace: 'normal' }}>
                            {s.adminNotes && <div>{s.adminNotes}</div>}
                            {s.flaggedReason && <div style={{ color: '#ffae3a', fontSize: '0.78rem' }}>{s.flaggedReason}</div>}
                            {s.voidReason && <div style={{ color: '#ff8a8a', fontSize: '0.78rem' }}>void: {s.voidReason}</div>}
                            {s.editReason && <div style={{ color: '#9aa3b4', fontSize: '0.78rem' }}>last edit: {s.editReason} ({s.editedByAdminName})</div>}
                        </td>
                        <td>
                            <Flex direction="row" gap="0.25rem">
                                {s.status !== 'VOIDED' && <Btn onClick={() => onEdit(s)}>Edit</Btn>}
                                {!s.clockOutUtc && s.status !== 'VOIDED' && <Btn onClick={() => onClose(s)}>Close</Btn>}
                                {s.status !== 'VOIDED' && <Btn color="secondary" onClick={() => onVoid(s)}>Void</Btn>}
                                <Btn color="secondary" onClick={() => onAudit(s._id)}>History</Btn>
                            </Flex>
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    </div>
);

// ── Modals ───────────────────────────────────────────────────────────

const ModalShell: React.FC<{ title: string; onClose: () => void; children: React.ReactNode }> = ({ title, onClose, children }) => (
    <div style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
    }}>
        <div style={{
            background: '#0e131c', border: '1px solid #2a3447', borderRadius: 10,
            padding: '1.1rem 1.2rem', minWidth: 320, maxWidth: 560, width: '90%',
            maxHeight: '85vh', overflowY: 'auto',
        }}>
            <Flex direction="row" gap="0.5rem">
                <h2 style={{ margin: 0, fontSize: '1.1rem', flex: 1 }}>{title}</h2>
                <Btn color="secondary" onClick={onClose}>✕</Btn>
            </Flex>
            <div style={{ marginTop: '0.75rem' }}>{children}</div>
        </div>
    </div>
);

// react-datetime-local needs YYYY-MM-DDTHH:mm format. Convert UTC ISO ↔ local string.
const isoToLocalInput = (iso: string | null | undefined): string => {
    if (!iso) return '';
    const d = new Date(iso);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const localInputToIso = (s: string): string | null => {
    if (!s) return null;
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? null : d.toISOString();
};

const EditShiftModal: React.FC<{
    shift: ShiftRow;
    onCancel: () => void;
    onSaved: () => void;
    flash: (msg: string, ok?: boolean) => void;
}> = ({ shift, onCancel, onSaved, flash }) => {
    const [clockIn, setClockIn] = useState(isoToLocalInput(shift.clockInUtc));
    const [clockOut, setClockOut] = useState(isoToLocalInput(shift.clockOutUtc));
    const [breakMin, setBreakMin] = useState(String(shift.unpaidBreakMinutes || 0));
    const [adminNotes, setAdminNotes] = useState(shift.adminNotes || '');
    const [reason, setReason] = useState('');
    const [confirmOverLimit, setConfirmOverLimit] = useState(false);
    const [saving, setSaving] = useState(false);

    const submit = async () => {
        if (!reason.trim()) { flash('Reason is required for every edit', false); return; }
        const cInIso = localInputToIso(clockIn);
        if (!cInIso) { flash('Invalid clock-in', false); return; }
        const cOutIso = clockOut ? localInputToIso(clockOut) : null;
        setSaving(true);
        try {
            await axios.patch(`/api/timeclock/punches/${shift._id}/edit`, {
                clockInUtc: cInIso,
                clockOutUtc: cOutIso,
                unpaidBreakMinutes: Number(breakMin) || 0,
                adminNotes,
                reason,
                confirmOverLimit,
            });
            onSaved();
        } catch (e: any) {
            const msg = e?.response?.data?.message || 'Edit failed';
            if (e?.response?.status === 422 && /16 hours/.test(msg) && !confirmOverLimit) {
                if (window.confirm('Shift exceeds 16 hours. Save as NEEDS_REVIEW?')) {
                    setConfirmOverLimit(true);
                    return;
                }
            }
            flash(msg, false);
        } finally { setSaving(false); }
    };

    return (
        <ModalShell title={`Edit shift — ${shift.employeeName}`} onClose={onCancel}>
            <Flex direction="column" gap="0.5rem">
                <label>Clock in <input type="datetime-local" value={clockIn} onChange={e => setClockIn(e.target.value)} /></label>
                <label>Clock out <input type="datetime-local" value={clockOut} onChange={e => setClockOut(e.target.value)} /></label>
                <label>Unpaid break (minutes) <input type="number" min={0} value={breakMin} onChange={e => setBreakMin(e.target.value)} /></label>
                <label>Admin notes <textarea rows={2} value={adminNotes} onChange={e => setAdminNotes(e.target.value)} /></label>
                <label>Reason <span style={{ color: '#ffae3a' }}>(required)</span>
                    <input type="text" value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. corrected forgot-to-clock-out" />
                </label>
                {(shift.originalClockInUtc || shift.originalClockOutUtc) && (
                    <div style={{ fontSize: '0.78rem', color: '#9aa3b4' }}>
                        Originals: {shift.originalClockInUtc && new Date(shift.originalClockInUtc).toLocaleString()}
                        {' → '}
                        {shift.originalClockOutUtc && new Date(shift.originalClockOutUtc).toLocaleString()}
                    </div>
                )}
                <Flex direction="row" gap="0.5rem">
                    <Btn onClick={onCancel}>Cancel</Btn>
                    <Btn color="primary" onClick={submit} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Btn>
                </Flex>
            </Flex>
        </ModalShell>
    );
};

const VoidShiftModal: React.FC<{
    shift: ShiftRow;
    onCancel: () => void;
    onConfirmed: () => void;
    flash: (msg: string, ok?: boolean) => void;
}> = ({ shift, onCancel, onConfirmed, flash }) => {
    const [reason, setReason] = useState('');
    const [busy, setBusy] = useState(false);
    const submit = async () => {
        if (!reason.trim()) { flash('Reason required', false); return; }
        setBusy(true);
        try {
            await axios.post(`/api/timeclock/punches/${shift._id}/void`, { reason });
            onConfirmed();
        } catch (e: any) {
            flash(e?.response?.data?.message || 'Void failed', false);
        } finally { setBusy(false); }
    };
    return (
        <ModalShell title={`Void shift — ${shift.employeeName}`} onClose={onCancel}>
            <p style={{ color: '#9aa3b4' }}>Voided shifts are kept for audit but excluded from hours.</p>
            <label>Reason <input type="text" value={reason} onChange={e => setReason(e.target.value)} /></label>
            <Flex direction="row" gap="0.5rem">
                <Btn onClick={onCancel}>Cancel</Btn>
                <Btn color="primary" onClick={submit} disabled={busy}>{busy ? 'Voiding…' : 'Void'}</Btn>
            </Flex>
        </ModalShell>
    );
};

const CloseShiftModal: React.FC<{
    shift: ShiftRow;
    onCancel: () => void;
    onConfirmed: () => void;
    flash: (msg: string, ok?: boolean) => void;
}> = ({ shift, onCancel, onConfirmed, flash }) => {
    const [clockOut, setClockOut] = useState(isoToLocalInput(new Date().toISOString()));
    const [reason, setReason] = useState('');
    const [adminNotes, setAdminNotes] = useState('');
    const [busy, setBusy] = useState(false);
    const submit = async () => {
        if (!reason.trim()) { flash('Reason required', false); return; }
        const cOutIso = localInputToIso(clockOut);
        if (!cOutIso) { flash('Invalid clock-out time', false); return; }
        setBusy(true);
        try {
            await axios.post(`/api/timeclock/punches/${shift._id}/close`, {
                clockOutUtc: cOutIso, reason, adminNotes,
            });
            onConfirmed();
        } catch (e: any) {
            flash(e?.response?.data?.message || 'Close failed', false);
        } finally { setBusy(false); }
    };
    return (
        <ModalShell title={`Close open shift — ${shift.employeeName}`} onClose={onCancel}>
            <p style={{ color: '#9aa3b4' }}>Open since {new Date(shift.clockInUtc).toLocaleString()}</p>
            <label>Clock out <input type="datetime-local" value={clockOut} onChange={e => setClockOut(e.target.value)} /></label>
            <label>Admin notes <textarea rows={2} value={adminNotes} onChange={e => setAdminNotes(e.target.value)} /></label>
            <label>Reason <input type="text" value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. employee forgot to clock out" /></label>
            <Flex direction="row" gap="0.5rem">
                <Btn onClick={onCancel}>Cancel</Btn>
                <Btn color="primary" onClick={submit} disabled={busy}>{busy ? 'Closing…' : 'Close shift'}</Btn>
            </Flex>
        </ModalShell>
    );
};

const AddManualShiftModal: React.FC<{
    employees: Employee[];
    onCancel: () => void;
    onSaved: () => void;
    flash: (msg: string, ok?: boolean) => void;
}> = ({ employees, onCancel, onSaved, flash }) => {
    const [employeeId, setEmployeeId] = useState('');
    const [clockIn, setClockIn] = useState(isoToLocalInput(new Date().toISOString()));
    const [clockOut, setClockOut] = useState('');
    const [breakMin, setBreakMin] = useState('0');
    const [adminNotes, setAdminNotes] = useState('');
    const [reason, setReason] = useState('');
    const [busy, setBusy] = useState(false);
    const submit = async () => {
        if (!employeeId) { flash('Pick an employee', false); return; }
        if (!reason.trim()) { flash('Reason required', false); return; }
        const cInIso = localInputToIso(clockIn);
        if (!cInIso) { flash('Invalid clock-in', false); return; }
        const cOutIso = clockOut ? localInputToIso(clockOut) : null;
        setBusy(true);
        try {
            await axios.post('/api/timeclock/punches/manual', {
                employeeId,
                clockInUtc: cInIso,
                clockOutUtc: cOutIso,
                unpaidBreakMinutes: Number(breakMin) || 0,
                adminNotes,
                reason,
            });
            onSaved();
        } catch (e: any) {
            flash(e?.response?.data?.message || 'Add failed', false);
        } finally { setBusy(false); }
    };
    return (
        <ModalShell title="Add manual shift" onClose={onCancel}>
            <Flex direction="column" gap="0.5rem">
                <label>Employee
                    <select value={employeeId} onChange={e => setEmployeeId(e.target.value)}>
                        <option value="">— pick employee —</option>
                        {employees.map(e => <option key={e._id} value={e._id}>{e.name}</option>)}
                    </select>
                </label>
                <label>Clock in <input type="datetime-local" value={clockIn} onChange={e => setClockIn(e.target.value)} /></label>
                <label>Clock out (optional) <input type="datetime-local" value={clockOut} onChange={e => setClockOut(e.target.value)} /></label>
                <label>Unpaid break (min) <input type="number" min={0} value={breakMin} onChange={e => setBreakMin(e.target.value)} /></label>
                <label>Admin notes <textarea rows={2} value={adminNotes} onChange={e => setAdminNotes(e.target.value)} /></label>
                <label>Reason <span style={{ color: '#ffae3a' }}>(required)</span>
                    <input type="text" value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. employee forgot to clock in for closing shift" />
                </label>
                <Flex direction="row" gap="0.5rem">
                    <Btn onClick={onCancel}>Cancel</Btn>
                    <Btn color="primary" onClick={submit} disabled={busy}>{busy ? 'Adding…' : 'Add shift'}</Btn>
                </Flex>
            </Flex>
        </ModalShell>
    );
};

const AuditHistoryModal: React.FC<{ shiftId: string; onClose: () => void }> = ({ shiftId, onClose }) => {
    const [rows, setRows] = useState<any[] | null>(null);
    useEffect(() => {
        axios.get(`/api/timeclock/punches/${shiftId}/audit`)
            .then(r => setRows(r.data))
            .catch(() => setRows([]));
    }, [shiftId]);
    return (
        <ModalShell title="Audit history" onClose={onClose}>
            {rows == null && <div>Loading…</div>}
            {rows && rows.length === 0 && <div style={{ color: '#9aa3b4' }}>No audit rows.</div>}
            {rows && rows.map((r: any) => (
                <div key={r._id} style={{
                    background: '#131822', border: '1px solid #1f2735',
                    borderRadius: 6, padding: '0.5rem 0.75rem', marginBottom: '0.5rem',
                    fontSize: '0.85rem',
                }}>
                    <div style={{ fontWeight: 600 }}>{r.action}</div>
                    <div style={{ color: '#9aa3b4', fontSize: '0.78rem' }}>
                        {new Date(r.createdAt).toLocaleString()} · {r.actorName || 'employee'} · {r.ipAddress || ''}
                    </div>
                    {r.reason && <div style={{ marginTop: 4 }}>{r.reason}</div>}
                    {r.beforeJson && (
                        <details>
                            <summary>before</summary>
                            <pre style={{ fontSize: '0.72rem', overflowX: 'auto' }}>{r.beforeJson}</pre>
                        </details>
                    )}
                    {r.afterJson && (
                        <details>
                            <summary>after</summary>
                            <pre style={{ fontSize: '0.72rem', overflowX: 'auto' }}>{r.afterJson}</pre>
                        </details>
                    )}
                </div>
            ))}
        </ModalShell>
    );
};

export default TimeClockAdminPage;
